import { useCallback, useEffect, useRef, useState } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { abortAllLive, abortJob, listLiveJobs } from '../api';
import { usePolling } from '../hooks/usePolling';
import type { Job } from '../types';

/**
 * Lightning-parity live-scans table for the interior sub-app.
 *
 * Cloned 1:1 from ``web/src/components/LiveScansTable.tsx`` and
 * rewired to use the interior API helpers (``listLiveJobs``,
 * ``abortJob``, ``abortAllLive``). The interior backend's
 * ``GET /api/jobs/live`` returns the same envelope as lightning's
 * — plus an ``elapsed_seconds`` field — so the table layout, status
 * badges, kill-confirm dialogs and live-ticking elapsed timer all work
 * unchanged. Abort here is wired to ``POST /api/jobs/{id}/abort``,
 * which now flips the row to ``failed`` synchronously so the UI
 * reflects the cancellation on the very next 3-second poll.
 */

function formatElapsedTime(seconds: number | null): string {
  if (seconds === null) return '-';

  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);

  if (mins > 0) {
    return `${mins}m ${secs}s`;
  }
  return `${secs}s`;
}

function ElapsedTimeCell({
  initialSeconds,
  status,
}: {
  initialSeconds: number | null | undefined;
  status: string;
}) {
  const initial = initialSeconds ?? null;
  const [elapsed, setElapsed] = useState<number | null>(initial);
  const lastInitialRef = useRef<number | null>(initial);

  useEffect(() => {
    if (initial !== lastInitialRef.current) {
      setElapsed(initial);
      lastInitialRef.current = initial;
    }
  }, [initial]);

  const isElapsedActive = elapsed !== null;
  useEffect(() => {
    if (status !== 'processing' || !isElapsedActive) return;

    const interval = setInterval(() => {
      setElapsed((prev) => (prev !== null ? prev + 1 : null));
    }, 1000);

    return () => clearInterval(interval);
  }, [status, isElapsedActive]);

  if (status === 'pending') {
    return <span className="text-slate-400">Waiting...</span>;
  }

  return (
    <span className="font-mono text-amber-600 font-medium">{formatElapsedTime(elapsed)}</span>
  );
}

function StatusBadge({ status }: { status: string }) {
  const baseClasses = 'px-3 py-1 rounded-full text-xs font-medium inline-flex items-center gap-2';

  if (status === 'processing') {
    return (
      <span className={`${baseClasses} status-processing processing-glow`}>
        <span className="w-1.5 h-1.5 rounded-full bg-current animate-pulse" />
        Processing
      </span>
    );
  }

  return (
    <span className={`${baseClasses} status-pending`}>
      <span className="w-1.5 h-1.5 rounded-full bg-current" />
      Pending
    </span>
  );
}

function SourceBadge({ source }: { source: string | null }) {
  if (!source) return <span className="text-slate-400">-</span>;

  if (source === 'manual') {
    return (
      <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-cyan-50 text-cyan-600">
        Manual
      </span>
    );
  }

  if (source === 'uvcamp') {
    return (
      <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-indigo-50 text-indigo-600">
        UVCamp
      </span>
    );
  }

  return (
    <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-600">
      {source}
    </span>
  );
}

function KillButton({
  onClick,
  isLoading,
  disabled,
}: {
  onClick: () => void;
  isLoading: boolean;
  disabled?: boolean;
}) {
  return (
    <button
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onClick();
      }}
      disabled={isLoading || disabled}
      className="px-2 py-1 text-xs font-medium text-[#F0544F] bg-[#F0544F]/10 hover:bg-[#F0544F]/20 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1 pointer-events-auto"
      title="Kill this job"
    >
      {isLoading ? (
        <svg className="w-3 h-3 animate-spin" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
        </svg>
      ) : (
        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      )}
      Kill
    </button>
  );
}

function UVCampLink({ link }: { link: string | null }) {
  if (!link) return <span className="text-slate-400">-</span>;
  return (
    <button
      type="button"
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        window.open(link, '_blank', 'noopener,noreferrer');
      }}
      className="text-indigo-600 hover:text-indigo-700 hover:underline font-medium cursor-pointer bg-transparent border-none p-0 pointer-events-auto"
    >
      Link
    </button>
  );
}

function ConfirmDialog({
  isOpen,
  title,
  message,
  confirmLabel,
  onConfirm,
  onCancel,
  isLoading,
}: {
  isOpen: boolean;
  title: string;
  message: string;
  confirmLabel: string;
  onConfirm: () => void;
  onCancel: () => void;
  isLoading: boolean;
}) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onCancel} />
      <div className="relative bg-white rounded-2xl shadow-xl p-6 max-w-md w-full mx-4">
        <h3 className="text-lg font-semibold text-slate-900 mb-2">{title}</h3>
        <p className="text-slate-600 mb-6">{message}</p>
        <div className="flex justify-end gap-3">
          <button
            onClick={onCancel}
            disabled={isLoading}
            className="px-4 py-2 text-sm font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={isLoading}
            className="px-4 py-2 text-sm font-medium text-white bg-[#F0544F] hover:bg-[#F0544F]/90 rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2"
          >
            {isLoading && (
              <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
            )}
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

function TableRow({
  job,
  onShowKillDialog,
  killingJobId,
  isKillingAll,
  collapsed,
  onToggle,
}: {
  job: Job;
  onShowKillDialog: (jobId: string) => void;
  killingJobId: string | null;
  isKillingAll: boolean;
  collapsed: boolean;
  onToggle: () => void;
}) {
  return (
    <tr
      className="hover:bg-slate-50 transition-colors"
      data-collapsed={collapsed}
      onClick={(e) => {
        if ((e.target as HTMLElement).closest('button, a, input, select')) return;
        onToggle();
      }}
    >
      <td className="px-5 py-3" data-label="Scan ID">
        <div className="flex items-center gap-2">
          <span className="font-mono text-indigo-600 font-medium">
            <span className="scan-id-short">{job.scan_id?.substring(0, 8)}</span>
            <span className="scan-id-full">{job.scan_id}</span>
          </span>
          {job.batch_id && (
            <span className="px-1.5 py-0.5 text-xs font-medium bg-indigo-50 text-indigo-600 rounded-md">
              Batch
            </span>
          )}
        </div>
      </td>
      <td className="px-5 py-3" data-label="UVCamp">
        <UVCampLink link={job.uvcamp_link} />
      </td>
      <td className="px-5 py-3" data-label="Source">
        <SourceBadge source={job.source} />
      </td>
      <td className="px-5 py-3" data-label="Organization">
        {job.organization_id ? (
          <span className="font-mono text-xs text-slate-500" title={job.organization_id}>
            {job.organization_id.length > 12
              ? `${job.organization_id.substring(0, 12)}...`
              : job.organization_id}
          </span>
        ) : (
          <span className="text-slate-400">-</span>
        )}
      </td>
      <td className="px-5 py-3" data-label="Status">
        <StatusBadge status={job.status} />
      </td>
      <td className="px-5 py-3" data-label="Queued">
        {job.created_at ? (
          <span className="text-slate-600">
            {formatDistanceToNow(new Date(job.created_at), { addSuffix: true })}
          </span>
        ) : (
          '-'
        )}
      </td>
      <td className="px-5 py-3" data-label="Elapsed Time">
        <ElapsedTimeCell initialSeconds={job.elapsed_seconds} status={job.status} />
      </td>
      <td className="px-5 py-3" data-label="Job ID">
        <span className="font-mono text-xs text-slate-400">{job.job_id.substring(0, 8)}...</span>
      </td>
      <td className="px-5 py-3" data-label="Actions">
        <KillButton
          onClick={() => onShowKillDialog(job.job_id)}
          isLoading={killingJobId === job.job_id}
          disabled={isKillingAll}
        />
      </td>
    </tr>
  );
}

export function LiveScansTable() {
  const { data, loading, refresh } = usePolling(listLiveJobs, 3000);
  const jobs = data ?? [];
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  // Mobile cards collapse to Scan ID / Status by default; tapping a card
  // expands it. Keyed by job_id so expansion survives the 3s poll.
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  const toggleRow = useCallback((jobId: string) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(jobId)) next.delete(jobId);
      else next.add(jobId);
      return next;
    });
  }, []);

  useEffect(() => {
    if (data !== null) {
      setLastUpdated(new Date());
    }
  }, [data]);

  const [killingJobId, setKillingJobId] = useState<string | null>(null);
  const [isKillingAll, setIsKillingAll] = useState(false);
  const [showKillAllDialog, setShowKillAllDialog] = useState(false);
  const [showKillJobDialog, setShowKillJobDialog] = useState<string | null>(null);

  const handleKillJob = useCallback(
    async (jobId: string) => {
      setKillingJobId(jobId);
      try {
        await abortJob(jobId);
        refresh();
      } catch (error) {
        alert(error instanceof Error ? error.message : 'Failed to abort job');
      } finally {
        setKillingJobId(null);
        setShowKillJobDialog(null);
      }
    },
    [refresh],
  );

  const handleKillAll = useCallback(async () => {
    setIsKillingAll(true);
    try {
      await abortAllLive();
      alert(`Aborted ${jobs.length} live job(s)`);
      refresh();
    } catch (error) {
      alert(error instanceof Error ? error.message : 'Failed to abort all jobs');
    } finally {
      setIsKillingAll(false);
      setShowKillAllDialog(false);
    }
  }, [refresh, jobs.length]);

  const handleShowKillDialog = useCallback((jobId: string) => {
    setShowKillJobDialog(jobId);
  }, []);

  return (
    <>
      <ConfirmDialog
        isOpen={showKillJobDialog !== null}
        title="Kill Job"
        message="Are you sure you want to kill this job? This action cannot be undone."
        confirmLabel="Kill Job"
        onConfirm={() => showKillJobDialog && handleKillJob(showKillJobDialog)}
        onCancel={() => setShowKillJobDialog(null)}
        isLoading={killingJobId !== null}
      />

      <ConfirmDialog
        isOpen={showKillAllDialog}
        title="Kill All Jobs"
        message={`Are you sure you want to kill all ${jobs.length} active jobs? This action cannot be undone.`}
        confirmLabel="Kill All"
        onConfirm={handleKillAll}
        onCancel={() => setShowKillAllDialog(false)}
        isLoading={isKillingAll}
      />

      <div className="bg-white rounded-2xl border border-slate-200 shadow-soft overflow-hidden transition-all duration-200">
        <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse" />
              <h2 className="text-base font-semibold text-slate-900">Live Scans</h2>
            </div>
            <p className="text-sm text-slate-500 mt-0.5">
              {jobs.length} active job{jobs.length !== 1 ? 's' : ''}
            </p>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            {lastUpdated && (
              <span className="text-xs text-slate-500">
                Updated {formatDistanceToNow(lastUpdated, { addSuffix: true })}
              </span>
            )}
            {jobs.length > 0 && (
              <button
                onClick={() => setShowKillAllDialog(true)}
                disabled={isKillingAll || killingJobId !== null}
                className="px-2.5 py-1 text-xs font-medium text-[#F0544F] bg-[#F0544F]/10 hover:bg-[#F0544F]/20 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5"
              >
                {isKillingAll ? (
                  <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                ) : (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                )}
                Kill All
              </button>
            )}
            <button
              onClick={refresh}
              className="btn-secondary px-2.5 py-1 rounded-md text-xs flex items-center gap-1.5"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Refresh
            </button>
          </div>
        </div>

        {loading && data === null ? (
          <div className="p-12 text-center">
            <div className="inline-flex items-center gap-3 text-slate-500">
              <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
              Loading...
            </div>
          </div>
        ) : jobs.length === 0 ? (
          <div className="p-12 text-center">
            <div className="inline-flex flex-col items-center gap-3">
              <div className="w-14 h-14 rounded-full bg-slate-100 flex items-center justify-center">
                <svg className="w-7 h-7 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <p className="text-slate-900 font-medium">No active scans</p>
                <p className="text-sm text-slate-500">All scans have completed or queue is empty</p>
              </div>
            </div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="data-table mobile-collapsible summary-inline">
              <thead>
                <tr>
                  <th className="px-5 py-3 text-left">Scan ID</th>
                  <th className="px-5 py-3 text-left">UVCamp</th>
                  <th className="px-5 py-3 text-left">Source</th>
                  <th className="px-5 py-3 text-left">Organization</th>
                  <th className="px-5 py-3 text-left">Status</th>
                  <th className="px-5 py-3 text-left">Queued</th>
                  <th className="px-5 py-3 text-left">Elapsed Time</th>
                  <th className="px-5 py-3 text-left">Job ID</th>
                  <th className="px-5 py-3 text-left">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {jobs.map((job) => (
                  <TableRow
                    key={job.job_id}
                    job={job}
                    onShowKillDialog={handleShowKillDialog}
                    killingJobId={killingJobId}
                    isKillingAll={isKillingAll}
                    collapsed={!expandedRows.has(job.job_id)}
                    onToggle={() => toggleRow(job.job_id)}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="px-5 py-3 border-t border-slate-100">
          <div className="flex items-center justify-between text-xs text-slate-500">
            <span>Auto-refreshing every 3 seconds</span>
            <span>Elapsed times update in real-time</span>
          </div>
        </div>
      </div>
    </>
  );
}
